import { useState } from 'react';
import { IndianRupee, Package, Percent, Star, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { KpiTile } from '@/components/charts/KpiTile';
import { FunnelChart } from '@/components/charts/FunnelChart';
import { DeltaBadge } from '@/components/charts/DeltaBadge';
import { EmptyState } from '@/components/charts/EmptyState';
import { MotionCard } from '@/components/motion/MotionCard';
import { useDashboard } from '@/hooks/useDashboardContext';
import { HeadToHead } from '@/components/charts/HeadToHead';
import { AISummary } from '@/components/charts/AISummary';
import { useAggregatorFunnel } from '@/hooks/useAggregatorFunnel';
import { useAggregatorMetric } from '@/hooks/useAggregatorMetric';
import { useOutletProviderBreakdown } from '@/hooks/useOutletProviderBreakdown';
import { useAdsByOutlet, type AdsOutletRow } from '@/hooks/useAdsByOutlet';
import { useDiscountsByOutlet, type DiscountOutletRow } from '@/hooks/useDiscountsByOutlet';
import { useOutlets } from '@/hooks/useOutlets';
import type { AggregatorProvider } from '@/lib/supabase/fooddb';
import { fmtISTRange, formatINRCompact, formatNumberCompact } from '@/lib/format';
import { cn } from '@/lib/utils';
import { AvailabilityPanel } from './AvailabilityPanel';

const PROVIDER_OPTIONS: { id: AggregatorProvider | 'both'; label: string }[] = [
  { id: 'both', label: 'Swiggy + Zomato' },
  { id: 'swiggy', label: 'Swiggy' },
  { id: 'zomato', label: 'Zomato' },
];

export function AggregatorsPage() {
  const { period, outletId } = useDashboard();
  const [providerSel, setProviderSel] = useState<AggregatorProvider | 'both'>('both');
  const providers: AggregatorProvider[] =
    providerSel === 'both' ? ['swiggy', 'zomato'] : [providerSel];

  const funnel = useAggregatorFunnel({ outletId, period, providers });
  const breakdown = useOutletProviderBreakdown({ period });

  const netSales = useAggregatorMetric({ outletId, period, concept: 'sales.net_sales', providers });
  const delivered = useAggregatorMetric({ outletId, period, concept: 'sales.delivered_orders', providers });
  const ordersPlaced = useAggregatorMetric({ outletId, period, concept: 'funnel.orders_placed', providers });

  // AOV is derived since Zomato doesn't expose it directly.
  const aovCurrent =
    netSales.data && delivered.data && delivered.data.metric.current > 0
      ? netSales.data.metric.current / delivered.data.metric.current
      : 0;
  const aovPrevious =
    netSales.data && delivered.data && delivered.data.metric.previous > 0
      ? netSales.data.metric.previous / delivered.data.metric.previous
      : 0;
  const aovDeltaPct = aovPrevious > 0 ? ((aovCurrent - aovPrevious) / aovPrevious) * 100 : 0;
  const aovComparable = !(aovPrevious === 0 && aovCurrent !== 0);

  const poorRated = useAggregatorMetric({ outletId, period, concept: 'ratings.poor_rated', providers });
  const totalRated = useAggregatorMetric({ outletId, period, concept: 'ratings.total_rated', providers });

  const adsSpend = useAggregatorMetric({ outletId, period, concept: 'ads.spend', providers });
  const adsOrders = useAggregatorMetric({ outletId, period, concept: 'ads.orders', providers });
  // True period ROAS = (ads_orders × aov) / ads_spend.
  // The legacy 'ads.roas' concept stores daily ratios; averaging them
  // explodes on low-spend days. Compute at the period level using the
  // already-fetched aggregates and the page-level AOV.
  const trueRoasCur =
    adsSpend.data && adsOrders.data && adsSpend.data.metric.current > 0 && aovCurrent > 0
      ? (adsOrders.data.metric.current * aovCurrent) / adsSpend.data.metric.current
      : 0;
  const trueRoasPrev =
    adsSpend.data && adsOrders.data && adsSpend.data.metric.previous > 0 && aovPrevious > 0
      ? (adsOrders.data.metric.previous * aovPrevious) / adsSpend.data.metric.previous
      : 0;
  const trueRoasDeltaPct =
    trueRoasPrev > 0 ? ((trueRoasCur - trueRoasPrev) / trueRoasPrev) * 100 : 0;
  const trueRoasMetric = {
    current: trueRoasCur,
    previous: trueRoasPrev,
    delta: trueRoasCur - trueRoasPrev,
    deltaPct: trueRoasDeltaPct,
    comparable: trueRoasPrev > 0,
  };

  const range = funnel.data?.current;
  const compareRange = funnel.data?.previous;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <SectionHeader
          title={
            <span className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-muted-foreground" />
              Aggregators
            </span>
          }
          description={
            range && compareRange
              ? `${range.label} · ${fmtISTRange(range.start, range.end)} vs ${fmtISTRange(compareRange.start, compareRange.end)}`
              : 'Swiggy + Zomato funnel, sales, ratings, and ads.'
          }
        />
        <ProviderToggle value={providerSel} onChange={setProviderSel} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="funnel">Funnel</TabsTrigger>
          <TabsTrigger value="ads">Ads</TabsTrigger>
          <TabsTrigger value="discounts">Discounts</TabsTrigger>
          <TabsTrigger value="availability">Availability</TabsTrigger>
        </TabsList>

        {/* ─────── Overview ─────── */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiTile
              label="Net sales (aggregators)"
              icon={<IndianRupee className="h-3.5 w-3.5" />}
              metric={netSales.data?.metric}
              loading={netSales.isLoading}
              format={formatINRCompact}
              sparkline={netSales.data?.trend.map((p) => p.current)}
              comparisonRange={netSales.data ? { start: netSales.data.previous.start, end: netSales.data.previous.end } : undefined}
            />
            <KpiTile
              label="Delivered orders"
              icon={<Package className="h-3.5 w-3.5" />}
              metric={delivered.data?.metric}
              loading={delivered.isLoading}
              format={formatNumberCompact}
              sparkline={delivered.data?.trend.map((p) => p.current)}
              comparisonRange={delivered.data ? { start: delivered.data.previous.start, end: delivered.data.previous.end } : undefined}
              delay={0.05}
            />
            <KpiTile
              label="Avg order value"
              icon={<IndianRupee className="h-3.5 w-3.5" />}
              metric={{
                current: aovCurrent,
                previous: aovPrevious,
                delta: aovCurrent - aovPrevious,
                deltaPct: aovDeltaPct,
                comparable: aovComparable,
              }}
              loading={netSales.isLoading || delivered.isLoading}
              format={formatINRCompact}
              comparisonRange={netSales.data ? { start: netSales.data.previous.start, end: netSales.data.previous.end } : undefined}
              delay={0.1}
            />
            <KpiTile
              label="Orders placed"
              icon={<Package className="h-3.5 w-3.5" />}
              metric={ordersPlaced.data?.metric}
              loading={ordersPlaced.isLoading}
              format={formatNumberCompact}
              sparkline={ordersPlaced.data?.trend.map((p) => p.current)}
              comparisonRange={ordersPlaced.data ? { start: ordersPlaced.data.previous.start, end: ordersPlaced.data.previous.end } : undefined}
              delay={0.15}
            />
          </div>

          <MotionCard delay={0.05}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-amber-500" /> Ratings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RatingsBreakdown
                  poorRated={poorRated.data?.metric.current ?? 0}
                  totalRated={totalRated.data?.metric.current ?? 0}
                  prevPoorRated={poorRated.data?.metric.previous ?? 0}
                  prevTotalRated={totalRated.data?.metric.previous ?? 0}
                  loading={poorRated.isLoading || totalRated.isLoading}
                />
              </CardContent>
            </Card>
          </MotionCard>

          <MotionCard delay={0.1}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Head-to-head: Swiggy vs Zomato
                  <span className="text-2xs font-normal text-muted-foreground">
                    per-outlet · current period
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 p-4 pt-0">
                <AISummary
                  outlets={breakdown.data?.outlets ?? []}
                  period={breakdown.data?.current.label ?? period}
                />
                <HeadToHead outlets={breakdown.data?.outlets ?? []} loading={breakdown.isLoading} />
              </CardContent>
            </Card>
          </MotionCard>
        </TabsContent>

        {/* ─────── Funnel ─────── */}
        <TabsContent value="funnel" className="space-y-6">
          <MotionCard>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Funnel · Impressions → Menu → Cart → Order
                  <span className="text-2xs font-normal text-muted-foreground">
                    {providerSel === 'both' ? 'Swiggy + Zomato' : providerSel}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FunnelChart stages={funnel.data?.stages ?? []} loading={funnel.isLoading} />
                <FunnelInsight stages={funnel.data?.stages ?? []} />
              </CardContent>
            </Card>
          </MotionCard>

          <SideBySideFunnels outletId={outletId} />

          <PerOutletFunnelMatrix />
        </TabsContent>

        {/* ─────── Ads ─────── */}
        <TabsContent value="ads" className="space-y-6">
          {/* Top-line: 4 KPI tiles (Spend / Orders / Ad revenue / True ROAS) */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiTile
              label="Ads spend"
              icon={<IndianRupee className="h-3.5 w-3.5" />}
              metric={adsSpend.data?.metric}
              loading={adsSpend.isLoading}
              format={formatINRCompact}
              sparkline={adsSpend.data?.trend.map((p) => p.current)}
              comparisonRange={adsSpend.data ? { start: adsSpend.data.previous.start, end: adsSpend.data.previous.end } : undefined}
            />
            <KpiTile
              label="Ads orders"
              icon={<Package className="h-3.5 w-3.5" />}
              metric={adsOrders.data?.metric}
              loading={adsOrders.isLoading}
              format={formatNumberCompact}
              sparkline={adsOrders.data?.trend.map((p) => p.current)}
              comparisonRange={adsOrders.data ? { start: adsOrders.data.previous.start, end: adsOrders.data.previous.end } : undefined}
              delay={0.05}
            />
            <KpiTile
              label="Ad-driven revenue"
              icon={<IndianRupee className="h-3.5 w-3.5" />}
              metric={{
                current: adsOrders.data ? adsOrders.data.metric.current * aovCurrent : 0,
                previous: adsOrders.data ? adsOrders.data.metric.previous * aovPrevious : 0,
                delta:
                  adsOrders.data
                    ? adsOrders.data.metric.current * aovCurrent -
                      adsOrders.data.metric.previous * aovPrevious
                    : 0,
                deltaPct: trueRoasDeltaPct,
                comparable: trueRoasMetric.comparable,
              }}
              loading={adsOrders.isLoading || netSales.isLoading}
              format={formatINRCompact}
              delay={0.1}
            />
            <KpiTile
              label="True ROAS"
              icon={<TrendingUp className="h-3.5 w-3.5" />}
              metric={trueRoasMetric}
              loading={adsSpend.isLoading || adsOrders.isLoading || netSales.isLoading}
              format={(n) => `${n.toFixed(2)}×`}
              delay={0.15}
            />
          </div>

          <PerOutletAdsMatrix providers={providers} />

          <Card className="border-dashed border-border/60 bg-muted/30">
            <CardContent className="p-4 text-2xs text-muted-foreground">
              <strong className="text-foreground">How True ROAS is computed:</strong>{' '}
              ad-driven revenue ÷ ad spend at the period level, where ad-driven revenue
              = ads orders × outlet AOV. This avoids the "average-of-daily-ratios"
              trap that explodes on low-spend days.
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─────── Discounts ─────── */}
        <TabsContent value="discounts" className="space-y-6">
          <DiscountsTab providers={providers} />
        </TabsContent>

        {/* ─────── Availability ─────── */}
        <TabsContent value="availability" className="space-y-6">
          <MotionCard>
            <AvailabilityPanel />
          </MotionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ───────── Funnel-tab components ─────────

function SideBySideFunnels({ outletId }: { outletId: string | 'all' }) {
  const { period } = useDashboard();
  const swiggy = useAggregatorFunnel({ outletId, period, providers: ['swiggy'] });
  const zomato = useAggregatorFunnel({ outletId, period, providers: ['zomato'] });
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <MotionCard>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full bg-orange-500" />
              Swiggy
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FunnelChart stages={swiggy.data?.stages ?? []} loading={swiggy.isLoading} />
            <FunnelInsight stages={swiggy.data?.stages ?? []} />
          </CardContent>
        </Card>
      </MotionCard>
      <MotionCard delay={0.05}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full bg-rose-500" />
              Zomato
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FunnelChart stages={zomato.data?.stages ?? []} loading={zomato.isLoading} />
            <FunnelInsight stages={zomato.data?.stages ?? []} />
          </CardContent>
        </Card>
      </MotionCard>
    </div>
  );
}

function PerOutletFunnelMatrix() {
  const { data: outlets = [] } = useOutlets();
  return (
    <MotionCard delay={0.1}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Funnel by outlet
            <span className="text-2xs font-normal text-muted-foreground">
              find the leakiest stage per outlet
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border/40 bg-muted/30 text-2xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Outlet</th>
                  <th className="px-3 py-2 text-right">Impressions</th>
                  <th className="px-3 py-2 text-right">CTR → menu</th>
                  <th className="px-3 py-2 text-right">Menu → cart</th>
                  <th className="px-3 py-2 text-right">Cart → order</th>
                </tr>
              </thead>
              <tbody>
                {outlets.map((outlet) => (
                  <PerOutletFunnelRow key={outlet.id} outletId={outlet.id} outletName={outlet.name} />
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </MotionCard>
  );
}

function PerOutletFunnelRow({ outletId, outletName }: { outletId: string; outletName: string }) {
  const { period } = useDashboard();
  const { data, isLoading } = useAggregatorFunnel({ outletId, period });
  const stages = data?.stages ?? [];
  const impressions = stages[0]?.metric.current ?? 0;
  const menuOpens = stages[1]?.metric.current ?? 0;
  const carts = stages[2]?.metric.current ?? 0;
  const orders = stages[3]?.metric.current ?? 0;
  const ctr = impressions > 0 ? (menuOpens / impressions) * 100 : 0;
  const cartRate = menuOpens > 0 ? (carts / menuOpens) * 100 : 0;
  const orderRate = carts > 0 ? (orders / carts) * 100 : 0;
  return (
    <tr className="border-b border-border/30 last:border-0">
      <td className="px-4 py-2 font-medium">{outletName}</td>
      <td className="px-3 py-2 text-right tabular-nums">{formatNumberCompact(impressions)}</td>
      <td className="px-3 py-2 text-right tabular-nums">
        <RateCell pct={ctr} loading={isLoading} thresholds={{ good: 3, warn: 1.5 }} />
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        <RateCell pct={cartRate} loading={isLoading} thresholds={{ good: 25, warn: 12 }} />
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        <RateCell pct={orderRate} loading={isLoading} thresholds={{ good: 60, warn: 40 }} />
      </td>
    </tr>
  );
}

function RateCell({
  pct,
  loading,
  thresholds,
}: {
  pct: number;
  loading: boolean;
  thresholds: { good: number; warn: number };
}) {
  if (loading) return <span className="text-muted-foreground">…</span>;
  if (pct === 0) return <span className="text-muted-foreground">—</span>;
  const tone =
    pct >= thresholds.good ? 'text-emerald-600' : pct >= thresholds.warn ? 'text-amber-600' : 'text-rose-600';
  return <span className={cn('font-semibold', tone)}>{pct.toFixed(1)}%</span>;
}

function FunnelInsight({ stages }: { stages: { key: string; metric: { current: number } }[] }) {
  if (stages.length < 4) return null;
  const impressions = stages[0]?.metric.current ?? 0;
  const menu = stages[1]?.metric.current ?? 0;
  const cart = stages[2]?.metric.current ?? 0;
  const orders = stages[3]?.metric.current ?? 0;
  if (impressions === 0) return null;

  const stageRates = [
    { label: 'CTR → menu', pct: (menu / impressions) * 100, good: 3, warn: 1.5 },
    { label: 'Menu → cart', pct: menu > 0 ? (cart / menu) * 100 : 0, good: 25, warn: 12 },
    { label: 'Cart → order', pct: cart > 0 ? (orders / cart) * 100 : 0, good: 60, warn: 40 },
  ];
  const weakest = stageRates.reduce((w, s) =>
    s.pct / Math.max(s.warn, 0.01) < w.pct / Math.max(w.warn, 0.01) ? s : w,
  );

  return (
    <div className="mt-4 grid grid-cols-3 gap-2 border-t border-border/40 pt-3 text-2xs">
      {stageRates.map((s) => {
        const tone =
          s.pct >= s.good ? 'text-emerald-600' : s.pct >= s.warn ? 'text-amber-600' : 'text-rose-600';
        const ringTone =
          s.pct >= s.good
            ? 'border-emerald-500/30'
            : s.pct >= s.warn
              ? 'border-amber-500/30'
              : 'border-rose-500/30';
        return (
          <div
            key={s.label}
            className={cn(
              'rounded-md border bg-card px-2 py-1.5',
              ringTone,
              s === weakest && 'shadow-sm',
            )}
          >
            <div className="text-muted-foreground">{s.label}</div>
            <div className={cn('font-semibold tabular-nums text-sm', tone)}>{s.pct.toFixed(1)}%</div>
          </div>
        );
      })}
    </div>
  );
}

// ───────── Existing helpers (unchanged) ─────────

function ProviderToggle({
  value,
  onChange,
}: {
  value: AggregatorProvider | 'both';
  onChange: (v: AggregatorProvider | 'both') => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-border/60 bg-card p-0.5 text-xs">
      {PROVIDER_OPTIONS.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={cn(
            'rounded px-2.5 py-1.5 transition-colors',
            value === o.id
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function RatingsBreakdown({
  poorRated,
  totalRated,
  prevPoorRated,
  prevTotalRated,
  loading,
}: {
  poorRated: number;
  totalRated: number;
  prevPoorRated: number;
  prevTotalRated: number;
  loading: boolean;
}) {
  if (loading) {
    return <div className="space-y-3 text-sm text-muted-foreground">Loading…</div>;
  }
  if (totalRated === 0) {
    return <EmptyState title="No ratings" description="No rated orders in this period." />;
  }
  const poorPct = (poorRated / totalRated) * 100;
  const prevPoorPct = prevTotalRated > 0 ? (prevPoorRated / prevTotalRated) * 100 : 0;
  const deltaPct = prevPoorPct > 0 ? ((poorPct - prevPoorPct) / prevPoorPct) * 100 : 0;
  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-muted-foreground">Poor-rated orders</span>
          <span className="font-mono tabular-nums">
            {formatNumberCompact(poorRated)} / {formatNumberCompact(totalRated)}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <span className="font-semibold tabular-nums text-2xl">{poorPct.toFixed(1)}%</span>
          <DeltaBadge deltaPct={deltaPct} invert comparable={prevPoorPct > 0} />
        </div>
        <div className="mt-2 h-2 w-full rounded-md bg-muted/30">
          <div
            className="h-full rounded-md bg-rose-500/80"
            style={{ width: `${Math.min(100, poorPct)}%` }}
          />
        </div>
      </div>
      <div className="text-2xs text-muted-foreground">
        Prior period: {prevPoorPct.toFixed(1)}% ({formatNumberCompact(prevPoorRated)} of{' '}
        {formatNumberCompact(prevTotalRated)})
      </div>
    </div>
  );
}

// ───────── Ads-tab: per-outlet matrix ─────────

function PerOutletAdsMatrix({ providers }: { providers: AggregatorProvider[] }) {
  const { period } = useDashboard();
  const { data: outlets = [] } = useOutlets();
  const { data, isLoading } = useAdsByOutlet({ period, providers });

  // Sort by spend desc so the biggest spenders surface first.
  const rows = (data?.rows ?? [])
    .map((r) => ({
      ...r,
      name: outlets.find((o) => o.id === r.outletId)?.name ?? r.outletId,
    }))
    .sort((a, b) => b.totalSpend - a.totalSpend);

  return (
    <MotionCard delay={0.05}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Ads by outlet
            <span className="text-2xs font-normal text-muted-foreground">
              spend, orders, true ROAS · Swiggy vs Zomato split
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 text-sm text-muted-foreground">Loading…</div>
          ) : rows.every((r) => r.totalSpend === 0) ? (
            <EmptyState
              title="No ad spend in this period"
              description="No outlet has spent on Swiggy or Zomato ads for the selected window."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border/40 bg-muted/30 text-2xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left">Outlet</th>
                    <th className="px-3 py-2 text-right">Spend</th>
                    <th className="px-3 py-2 text-right">Ad orders</th>
                    <th className="px-3 py-2 text-right">Ad revenue</th>
                    <th className="px-3 py-2 text-right">True ROAS</th>
                    <th className="px-3 py-2 text-right">Swiggy</th>
                    <th className="px-3 py-2 text-right">Zomato</th>
                    <th className="px-3 py-2 text-left">Best</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <AdsRow key={r.outletId} row={r} />
                  ))}
                  {data && (
                    <tr className="border-t-2 border-border/60 bg-muted/30 font-semibold">
                      <td className="px-4 py-2 text-left">Total</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatINRCompact(data.totals.spend)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatNumberCompact(data.totals.orders)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatINRCompact(data.totals.revenue)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <RoasCell roas={data.totals.roas} />
                      </td>
                      <td className="px-3 py-2" colSpan={3} />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </MotionCard>
  );
}

function AdsRow({ row }: { row: AdsOutletRow & { name: string } }) {
  return (
    <tr className="border-b border-border/30 last:border-0">
      <td className="px-4 py-2 font-medium">{row.name}</td>
      <td className="px-3 py-2 text-right tabular-nums">{formatINRCompact(row.totalSpend)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{formatNumberCompact(row.totalOrders)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{formatINRCompact(row.totalAdRevenue)}</td>
      <td className="px-3 py-2 text-right tabular-nums">
        <RoasCell roas={row.combinedRoas} />
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        <PlatformCell platformSpend={row.swiggy.spend} roas={row.swiggy.roas} />
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        <PlatformCell platformSpend={row.zomato.spend} roas={row.zomato.roas} />
      </td>
      <td className="px-3 py-2 text-left">
        {row.bestPlatform === 'swiggy' && (
          <span className="inline-flex items-center gap-1 rounded-full bg-orange-500/10 px-2 py-0.5 text-2xs font-medium text-orange-700 dark:text-orange-300">
            <span className="h-1.5 w-1.5 rounded-full bg-orange-500" /> Swiggy
          </span>
        )}
        {row.bestPlatform === 'zomato' && (
          <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/10 px-2 py-0.5 text-2xs font-medium text-rose-700 dark:text-rose-300">
            <span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> Zomato
          </span>
        )}
        {row.bestPlatform === null && (
          <span className="text-2xs text-muted-foreground">—</span>
        )}
      </td>
    </tr>
  );
}

function RoasCell({ roas }: { roas: number }) {
  if (roas === 0) return <span className="text-muted-foreground">—</span>;
  const tone =
    roas >= 4 ? 'text-emerald-600' : roas >= 2 ? 'text-amber-600' : 'text-rose-600';
  return <span className={cn('font-semibold', tone)}>{roas.toFixed(2)}×</span>;
}

function PlatformCell({ platformSpend, roas }: { platformSpend: number; roas: number }) {
  if (platformSpend === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-col items-end leading-tight">
      <span className="text-xs">{formatINRCompact(platformSpend)}</span>
      <span className={cn(
        'text-2xs',
        roas >= 4 ? 'text-emerald-600' : roas >= 2 ? 'text-amber-600' : 'text-rose-600',
      )}>
        {roas.toFixed(2)}× ROAS
      </span>
    </div>
  );
}

// ───────── Discounts tab ─────────

function DiscountsTab({ providers }: { providers: AggregatorProvider[] }) {
  const { period } = useDashboard();
  const { data: outlets = [] } = useOutlets();
  const { data, isLoading } = useDiscountsByOutlet({ period, providers });

  const rows = (data?.rows ?? [])
    .map((r) => ({
      ...r,
      name: outlets.find((o) => o.id === r.outletId)?.name ?? r.outletId,
    }))
    .sort((a, b) => b.combinedPctOfGross - a.combinedPctOfGross);

  const t = data?.totals;
  const effectiveAovAfterDiscount = t && t.delivered > 0 ? t.netSales / t.delivered : 0;
  const grossAov = t && t.delivered > 0 ? (t.netSales + t.discountRs) / t.delivered : 0;
  const aovErosion = grossAov - effectiveAovAfterDiscount;

  // Two worst-offender outlets for the callout
  const overDiscounters = rows.filter((r) => r.combinedPctOfGross > 20).slice(0, 3);
  const orderTakeRate =
    t && t.delivered > 0 ? (t.ordersWithOffer / t.delivered) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* 4 KPI tiles */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Discount given (restaurant)"
          icon={<IndianRupee className="h-3.5 w-3.5" />}
          metric={
            t
              ? {
                  current: t.discountRs,
                  previous: 0,
                  delta: 0,
                  deltaPct: 0,
                  comparable: false,
                }
              : undefined
          }
          loading={isLoading}
          format={formatINRCompact}
        />
        <KpiTile
          label="Discount % of GMV"
          icon={<Percent className="h-3.5 w-3.5" />}
          metric={
            t
              ? {
                  current: t.pctOfGross,
                  previous: 0,
                  delta: 0,
                  deltaPct: 0,
                  comparable: false,
                }
              : undefined
          }
          loading={isLoading}
          format={(n) => `${n.toFixed(1)}%`}
          delay={0.05}
        />
        <KpiTile
          label="₹ per order"
          icon={<IndianRupee className="h-3.5 w-3.5" />}
          metric={
            t
              ? {
                  current: t.perOrder,
                  previous: 0,
                  delta: 0,
                  deltaPct: 0,
                  comparable: false,
                }
              : undefined
          }
          loading={isLoading}
          format={(n) => `₹${Math.round(n)}`}
          delay={0.1}
        />
        <KpiTile
          label="AOV erosion"
          icon={<IndianRupee className="h-3.5 w-3.5" />}
          metric={
            t
              ? {
                  current: aovErosion,
                  previous: 0,
                  delta: 0,
                  deltaPct: 0,
                  comparable: false,
                }
              : undefined
          }
          loading={isLoading}
          format={(n) => `₹${Math.round(n)}`}
          delay={0.15}
        />
      </div>

      {/* Over-discounter callout */}
      {overDiscounters.length > 0 && (
        <Card className="border-rose-500/30 bg-rose-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">
              <Percent className="h-3.5 w-3.5" />
              {overDiscounters.length} outlet{overDiscounters.length > 1 ? 's' : ''} over 20% discount of GMV
            </div>
            <ul className="mt-2 grid gap-1 md:grid-cols-3">
              {overDiscounters.map((r) => (
                <li key={r.outletId} className="text-sm">
                  <span className="font-medium">{r.name}</span>{' '}
                  <span className="text-rose-600 dark:text-rose-400 tabular-nums">
                    {r.combinedPctOfGross.toFixed(1)}%
                  </span>
                  <span className="text-2xs text-muted-foreground">
                    {' '}
                    · {formatINRCompact(r.totalDiscountRs)} given
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-2 text-2xs text-muted-foreground">
              Above 20% usually means an offer is pulling traffic that won't return at full price. Decide: keep
              the volume trade-off or pull the offer.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-outlet matrix */}
      <MotionCard delay={0.05}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Discounts by outlet
              <span className="text-2xs font-normal text-muted-foreground">
                restaurant-funded · sorted by discount % of GMV
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-4 text-sm text-muted-foreground">Loading…</div>
            ) : rows.every((r) => r.totalDiscountRs === 0) ? (
              <EmptyState
                title="No discounts in this period"
                description="No outlet has restaurant-funded discount on Swiggy or Zomato for the selected window."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-border/40 bg-muted/30 text-2xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-left">Outlet</th>
                      <th className="px-3 py-2 text-right">Discount ₹</th>
                      <th className="px-3 py-2 text-right">% of GMV</th>
                      <th className="px-3 py-2 text-right">₹/order</th>
                      <th className="px-3 py-2 text-right">Swiggy</th>
                      <th className="px-3 py-2 text-right">Zomato</th>
                      <th className="px-3 py-2 text-left">Heaviest on</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <DiscountsRow key={r.outletId} row={r} />
                    ))}
                    {t && (
                      <tr className="border-t-2 border-border/60 bg-muted/30 font-semibold">
                        <td className="px-4 py-2 text-left">Total</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatINRCompact(t.discountRs)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          <DiscPctCell pct={t.pctOfGross} />
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">₹{Math.round(t.perOrder)}</td>
                        <td className="px-3 py-2" colSpan={3} />
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </MotionCard>

      {/* Offer take-rate (Zomato only — Swiggy doesn't expose this directly) */}
      {t && t.ordersWithOffer > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Offer take-up <span className="text-2xs font-normal text-muted-foreground">Zomato</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Stat label="Orders with an offer" value={formatNumberCompact(t.ordersWithOffer)} />
            <Stat label="Take rate" value={`${orderTakeRate.toFixed(1)}%`} />
            <Stat label="GMV from offers" value={formatINRCompact(t.gmvFromOffers)} />
            <Stat
              label="Offer-attributed share"
              value={
                t.netSales > 0
                  ? `${((t.gmvFromOffers / t.netSales) * 100).toFixed(1)}%`
                  : '—'
              }
            />
          </CardContent>
        </Card>
      )}

      <Card className="border-dashed border-border/60 bg-muted/30">
        <CardContent className="p-4 text-2xs text-muted-foreground">
          <strong className="text-foreground">How these numbers are computed:</strong>{' '}
          period-level <code>sum(discount ₹) / sum(net + discount)</code> for % of GMV — never an
          average of daily percentages. ₹/order uses delivered orders, not orders placed (canceled
          orders don't owe discount). Only restaurant-funded discount is shown; platform-funded
          subsidies aren't exposed by Swiggy / Zomato exports.
        </CardContent>
      </Card>
    </div>
  );
}

function DiscountsRow({ row }: { row: DiscountOutletRow & { name: string } }) {
  return (
    <tr className="border-b border-border/30 last:border-0">
      <td className="px-4 py-2 font-medium">{row.name}</td>
      <td className="px-3 py-2 text-right tabular-nums">{formatINRCompact(row.totalDiscountRs)}</td>
      <td className="px-3 py-2 text-right tabular-nums">
        <DiscPctCell pct={row.combinedPctOfGross} />
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {row.combinedPerOrder > 0 ? `₹${Math.round(row.combinedPerOrder)}` : '—'}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        <DiscPlatformCell discountRs={row.swiggy.discountRs} pct={row.swiggy.discountPctOfGross} />
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        <DiscPlatformCell discountRs={row.zomato.discountRs} pct={row.zomato.discountPctOfGross} />
      </td>
      <td className="px-3 py-2 text-left">
        {row.worstPlatform === 'swiggy' && (
          <span className="inline-flex items-center gap-1 rounded-full bg-orange-500/10 px-2 py-0.5 text-2xs font-medium text-orange-700 dark:text-orange-300">
            <span className="h-1.5 w-1.5 rounded-full bg-orange-500" /> Swiggy
          </span>
        )}
        {row.worstPlatform === 'zomato' && (
          <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/10 px-2 py-0.5 text-2xs font-medium text-rose-700 dark:text-rose-300">
            <span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> Zomato
          </span>
        )}
        {row.worstPlatform === null && (
          <span className="text-2xs text-muted-foreground">—</span>
        )}
      </td>
    </tr>
  );
}

function DiscPctCell({ pct }: { pct: number }) {
  if (pct === 0) return <span className="text-muted-foreground">—</span>;
  const tone =
    pct < 15 ? 'text-emerald-600' : pct < 20 ? 'text-amber-600' : 'text-rose-600';
  return <span className={cn('font-semibold', tone)}>{pct.toFixed(1)}%</span>;
}

function DiscPlatformCell({ discountRs, pct }: { discountRs: number; pct: number }) {
  if (discountRs === 0) return <span className="text-muted-foreground">—</span>;
  const tone =
    pct < 15 ? 'text-emerald-600' : pct < 20 ? 'text-amber-600' : 'text-rose-600';
  return (
    <div className="flex flex-col items-end leading-tight">
      <span className="text-xs">{formatINRCompact(discountRs)}</span>
      <span className={cn('text-2xs', tone)}>{pct.toFixed(1)}%</span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col leading-tight">
      <span className="text-base font-semibold tabular-nums md:text-lg">{value}</span>
      <span className="text-2xs uppercase tracking-wide text-muted-foreground">{label}</span>
    </div>
  );
}
